import type { FieldPath, FieldValues } from "react-hook-form";
import { CdcSelectField, type CdcSelectFieldProps } from "./cdc-field-helpers";
import { getCdcRoleOptions } from "@/i18n/cdc-options";

export type CdcRoleFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<CdcSelectFieldProps<TFieldValues, TName>, "fieldKey" | "options"> & {
  /**
   * Optional list of options used only to filter the localized role list by `id`.
   *
   * Note:
   * - Only `id` is used for filtering.
   * - Passed `label` and `value` are ignored.
   * - Labels and values are always resolved from localized CMS data.
   */
  options?: CdcSelectFieldProps<TFieldValues, TName>["options"];
};

export function CdcRoleField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  locale,
  options,
  ...rest
}: Readonly<CdcRoleFieldProps<TFieldValues, TName>>) {
  const localizedOptions = getCdcRoleOptions(locale);
  const resolvedOptions =
    options && options.length > 0
      ? localizedOptions.filter((opt) =>
          options.some((passed) => passed.id === opt.id),
        )
      : localizedOptions;
  return (
    <CdcSelectField
      {...rest}
      fieldKey="role"
      locale={locale}
      options={resolvedOptions}
    />
  );
}
